import React, { useMemo, useState, useEffect } from 'react';
import {
  set,
  get,
  getOr,
  has,
  map,
  some,
  reject,
  omit,
  isEmpty,
  toLower
} from 'lodash/fp';
import PropTypes from 'prop-types';
import {
  Button,
  Icon,
  Input,
  HelpBlock,
  FormControl,
  ErrorMessage
} from 'rsuite';
import {
  composeLimitRulesOptionKey,
  limitRulesMapToOptions
} from '../../../helpers/limitRules';
import { SelectOptionsType } from '../../../interfaces';
import { PicklistCategoryType } from '../../../interfaces/reservation';
import { ControlLabel, FormGroup } from '../../widgets/FormElements';
import LimitRulesModal from '../LimitRulesModal';
import CategoryItemsFormGroup from '../CategoryItemsFormGroup';
import {
  StyledFormGroup,
  CustomFormControl,
  LimitRulesSelectPicker
} from './elements';

const CategoryExistsError = 'Category already exists';

const CategoriesFormGroup = ({
  data,
  defaultValue,
  limitRulesMap,
  itemOptions,
  onChange,
  onCheck
}) => {
  const [newCategory, setNewCategory] = useState(defaultValue);
  const [showLimitRules, setShowLimitRules] = useState(false);
  const [newNameExists, setNewNameExists] = useState(false);
  const [fieldError, setFieldError] = useState({});

  const limitRulesOptions = useMemo(
    () => limitRulesMapToOptions(limitRulesMap),
    [limitRulesMap]
  );
  const disabledAddCategory = !newCategory.name || newNameExists;

  // New Category Handlers
  const handleNameChange = name => {
    setNewCategory(set('name', name, newCategory));
  };

  const handleNameBlur = ({ target }) => {
    const nameExists = some(
      ({ name }) => toLower(name) === toLower(target.value),
      data
    );
    setNewNameExists(nameExists);
  };

  const handleSelectLimitRule = value => {
    const isCreateNew = value === 0;
    if (isCreateNew) {
      setShowLimitRules(true);
      setNewCategory(set('position', 0, newCategory));
    } else setNewCategory(set('limitRules', limitRulesMap[value], newCategory));
  };

  // Edit Handlers
  const handleEditNameChange = i => ({ target }) => {
    const { value: name } = target;
    if (!name) {
      setFieldError(set(i, 'This field is required', fieldError));
      return;
    }

    const clonedCategories = [...data];
    clonedCategories.splice(i, 1);

    const isNameExists = some({ name }, clonedCategories);
    if (isNameExists) setFieldError(set(i, CategoryExistsError, fieldError));
    else {
      setFieldError(omit(i, fieldError));
      const items = map(item => ({ ...item, category: name }), data[i].items);
      onChange(set(i, { ...data[i], name, items }, data));
    }
  };

  const handleEditSelectLimitRule = i => value => {
    const isCreateNew = value === 0;
    if (isCreateNew) {
      setShowLimitRules(true);
      setNewCategory(set('position', i + 1, newCategory));
    } else onChange(set(`${i}.limitRules`, limitRulesMap[value], data));
  };

  const handleChangeItem = i => value => {
    const items = value.map((item, key) => ({ ...item, position: key + 1 }));
    onChange(set(`${i}.items`, items, data));
  };

  const handleCheckItem = error => {
    onCheck(error);
  };
  // End of Edit Handlers

  const handleHideLimitRules = () => {
    setShowLimitRules(false);
  };

  const handleSaveLimitRules = value => {
    setShowLimitRules(false);

    // Edit Limit Rule
    if (newCategory.position > 0) {
      onChange(set(`${newCategory.position - 1}.limitRules`, value, data));
      return;
    }

    if (newCategory.name) {
      setNewCategory(defaultValue);
      onChange([...data, set('limitRules', value, newCategory)]);
    } else {
      setNewCategory(set('limitRules', value, newCategory));
    }
  };

  const handleToggleCategory = i => () => {
    const isVisible = !getOr(true, `${i}.isVisible`, data);
    onChange(set(`${i}.isVisible`, isVisible, data));
    if (has(i, fieldError)) setFieldError(omit(i, fieldError));
  };

  const handleDeleteCategory = (i, name) => () => {
    onChange(reject({ name }, data));
    if (has(i, fieldError)) setFieldError(omit(i, fieldError));
  };

  const handleSaveCategory = () => {
    onChange([...data, newCategory]);
    setNewCategory(defaultValue);
  };

  useEffect(() => setNewCategory(defaultValue), [defaultValue]);

  useEffect(
    () => {
      onCheck(fieldError);
    },
    [fieldError]
  );

  return (
    <FormGroup>
      <ControlLabel>Categories ({data.length}) *</ControlLabel>
      <div style={{ marginLeft: 182 }}>
        {data.map((category, i) => {
          const isVisible = getOr(true, 'isVisible', category);
          return (
            <StyledFormGroup
              key={`category-${category.name}`}
              className={isVisible ? '' : 'opaq'}
            >
              <CustomFormControl>
                <Input
                  width={418}
                  style={{ width: 245, marginRight: 8 }}
                  defaultValue={category.name}
                  onBlur={handleEditNameChange(i)}
                />
                {has(i, fieldError) && (
                  <ErrorMessage style={{ zIndex: 10 }} show>
                    {get(i, fieldError)}
                  </ErrorMessage>
                )}
              </CustomFormControl>
              <LimitRulesSelectPicker
                placeholder="Select Category Limit Rule"
                value={composeLimitRulesOptionKey(category.limitRules)}
                style={{ width: 306, marginRight: 8 }}
                onChange={handleEditSelectLimitRule(i)}
                data={limitRulesOptions}
              />
              <Button
                onClick={handleDeleteCategory(i, category.name)}
                style={{ marginRight: 8 }}
              >
                <Icon icon="trash" style={{ color: 'red' }} />
              </Button>
              <Button onClick={handleToggleCategory(i)}>
                <Icon
                  icon={isVisible ? 'eye' : 'eye-slash'}
                  style={{ color: isVisible ? 'green' : 'gray' }}
                />
              </Button>
              <CategoryItemsFormGroup
                data={category.items}
                category={category.name}
                itemOptions={itemOptions}
                limitRulesMap={limitRulesMap}
                onChange={handleChangeItem(i)}
                onCheck={handleCheckItem}
              />
            </StyledFormGroup>
          );
        })}
        <FormGroup>
          <FormControl
            placeholder="Category Name"
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            style={{ width: 260, marginRight: 10 }}
            value={newCategory.name}
            errorMessage={newNameExists && CategoryExistsError}
          />
          <LimitRulesSelectPicker
            cleanable={false}
            placeholder="Select Category Limit Rule"
            value={composeLimitRulesOptionKey(newCategory.limitRules)}
            style={{ width: 300, marginRight: 10 }}
            onChange={handleSelectLimitRule}
            disabled={!newCategory.name || newNameExists}
            data={limitRulesOptions}
          />
          <Button disabled={disabledAddCategory} onClick={handleSaveCategory}>
            <Icon
              icon="plus-circle"
              style={!disabledAddCategory ? { color: 'green' } : {}}
            />
          </Button>
        </FormGroup>
        {isEmpty(data) && (
          <FormControl name="categories" type="hidden" value="" />
        )}
      </div>
      <HelpBlock style={{ marginLeft: 182 }}>
        Categories allow you to breakup the selections into understandable sub
        collections. <br />
        Examples are Produce, Staples, Breads, Protiens etc.
      </HelpBlock>
      <LimitRulesModal
        show={showLimitRules}
        onSave={handleSaveLimitRules}
        onCancel={handleHideLimitRules}
      />
    </FormGroup>
  );
};

CategoriesFormGroup.propTypes = {
  data: PropTypes.arrayOf(PicklistCategoryType),
  defaultValue: PropTypes.shape({}).isRequired,
  limitRulesMap: PropTypes.shape({}),
  itemOptions: SelectOptionsType.isRequired,
  onChange: PropTypes.func.isRequired,
  onCheck: PropTypes.func.isRequired
};

CategoriesFormGroup.defaultProps = {
  data: [],
  limitRulesMap: {}
};

export default CategoriesFormGroup;
